/**
 * One widget → its draw operations, in the vocabulary `plate-chrome.ts`
 * measured. Nothing here decides *where* anything goes: the screen models put
 * the rectangles in, this turns them into pixels, and the same rectangles are
 * what the hit test reads, so a control cannot be drawn where the mouse
 * cannot find it.
 */

import { boardButtonOps } from "./board-chrome.ts";
import { bevelRings, type DrawOp, fill, type FontId, type Rect, type TextStyle } from "./draw-ops.ts";
import {
  ARROW_SIZE,
  arrowOps,
  chamferedPlateOps,
  embossOn,
  ORANGE_SUNKEN_RINGS,
  LIST_FONT,
  LIST_ROW_PITCH,
  LIST_ROW_TEXT_INSET_X,
  LIST_SELECTED_TEXT,
  LIST_TEXT,
  listSelectionOps,
  PANEL_FILL,
  PLATE_FILL,
  plateTextX,
  raisedPlateOps,
  ruleFanOps,
  slotOps,
  SLOT_FILL,
  SLOT_TEXT_INSET,
  sunkenWellOps,
} from "./plate-chrome.ts";
import type { TextRole, Widget } from "../model/shell-screen.ts";
import type { FontAtlas } from "./text-metrics.ts";

/** The orange plate face every raised value chip and the debriefing's stats plate share. */
const CHIP_PLATE = 249;
const CHIP_DISABLED_PLATE = 233;
/** Flat ink for a value printed on black — no emboss, measured on every readout. */
const WELL_TEXT = 248;
/**
 * How far inside a left-aligned well's frame the value's first ink column sits.
 *
 * Measured off `mission-debriefing.png` itself rather than read out of a note:
 * `sunkenWellOps` sinks a well's floor two pixels inside its rect, the capture's
 * floors are at x=47 (the 66-wide MISSION SCORE well, rect.x=45) and x=73 (the
 * 17-wide AIRCRAFT LOST well, rect.x=71), and both values' first ink is two
 * columns inside the floor — so four inside the rect, on both widths. FONT3's
 * digits carry no left bearing (`'0'` inks column 0 of its own box), so the
 * glyph's x *is* its first ink column.
 *
 * `docs/game/end-of-mission.md` recorded this as `well.x + 5`; the capture says
 * 4, and `well-alignment.test.ts` measures the PNG rather than restating either
 * number.
 */
const WELL_TEXT_INSET = 4;

export interface WidgetFonts {
  readonly heading: FontAtlas;
  readonly small: FontAtlas;
}

interface RoleInk {
  readonly font: FontId;
  readonly style: TextStyle;
}

const ROLE_INK: Readonly<Record<TextRole, RoleInk>> = {
  heading: { font: "FONT1", style: embossOn(PLATE_FILL) },
  prompt: { font: "FONT3", style: embossOn(PLATE_FILL) },
  label: { font: "FONT3", style: embossOn(PLATE_FILL) },
  prose: { font: "FONT3", style: embossOn(PANEL_FILL) },
  slot: { font: "FONT3", style: embossOn(SLOT_FILL) },
  orangeHeading: { font: "FONT1", style: embossOn(CHIP_PLATE, true) },
  orangeLabel: { font: "FONT3", style: embossOn(CHIP_PLATE, true) },
  talk: { font: "TALK", style: embossOn(PANEL_FILL) },
};

export function textOp(role: TextRole, text: string, x: number, y: number): DrawOp {
  const ink = ROLE_INK[role];
  return { kind: "text", font: ink.font, text, x, y, style: ink.style };
}

/**
 * A chip: the raised tan frame with an orange plate sunk into it that the
 * Realism Panel uses for every toggle label and selector, and Flight
 * Assignments for every value field. Measured identically on both — one raised
 * ring `[224,236,234]`, a one-pixel `228` gap, one orange ring
 * `[253,245,245]`, then `249`. The plate is two pixels in and four smaller
 * each way, which makes a 13-tall chip a 9-tall plate.
 */
export function chipPlateRect(rect: Rect): Rect {
  return { x: rect.x + 2, y: rect.y + 2, w: rect.w - 4, h: rect.h - 4 };
}

function chipOps(rect: Rect, enabled: boolean, focused: boolean): DrawOp[] {
  const plate = chipPlateRect(rect);
  const face = enabled ? CHIP_PLATE : CHIP_DISABLED_PLATE;
  return [
    ...bevelRings(rect, [[224, 236, 234]]),
    fill({ x: rect.x + 1, y: rect.y + 1, w: rect.w - 2, h: rect.h - 2 }, PLATE_FILL),
    ...bevelRings(plate, [[focused ? 254 : 253, 245, 245]]),
    fill({ x: plate.x + 1, y: plate.y + 1, w: plate.w - 2, h: plate.h - 2 }, face),
  ];
}

function chipTextOps(rect: Rect, text: string, enabled: boolean, font: FontAtlas, dy: number): DrawOp {
  const plate = chipPlateRect(rect);
  return {
    kind: "text",
    font: LIST_FONT,
    text,
    x: plateTextX(font, text, plate),
    y: plate.y + dy,
    style: embossOn(enabled ? CHIP_PLATE : CHIP_DISABLED_PLATE, true),
  };
}

/** Flight Assignments puts its value two rows into the plate; the Realism Panel's labels sit one lower. */
function valueOps(widget: Extract<Widget, { kind: "value" }>, fonts: WidgetFonts, focused: boolean): DrawOp[] {
  return [
    ...chipOps(widget.rect, widget.enabled, focused),
    chipTextOps(widget.rect, widget.text, widget.enabled, fonts.small, 2),
  ];
}

/**
 * A Realism Panel checkbox: an 11×11 sunken frame around a 7×7 black well,
 * beside a chip carrying the label. The tick is `ESNTL.BMP` frame 10 — an 8×5
 * sprite the game already ships — drawn one row into the well, which
 * template-matched 33 of its 35 opaque pixels against the capture.
 */
export const CHECK_SHEET = "sprites/ESNTL.BMP";
export const CHECK_FRAME = 10;

function toggleOps(widget: Extract<Widget, { kind: "toggle" }>, fonts: WidgetFonts, focused: boolean): DrawOp[] {
  const ops: DrawOp[] = [...sunkenWellOps(widget.box), ...chipOps(widget.plate, widget.enabled, focused)];
  if (widget.checked) {
    ops.push({ kind: "sprite", sheet: CHECK_SHEET, frame: CHECK_FRAME, x: widget.box.x + 2, y: widget.box.y + 3, flipH: false });
  }
  ops.push(chipTextOps(widget.plate, widget.label, widget.enabled, fonts.small, 3));
  return ops;
}

function listOps(widget: Extract<Widget, { kind: "list" }>, focused: boolean): DrawOp[] {
  const well: Rect = { x: widget.rect.x + 2, y: widget.rect.y + 2, w: widget.rect.w - 4, h: widget.rect.h - 4 };
  const visible = Math.floor(well.h / LIST_ROW_PITCH);
  const ops: DrawOp[] = sunkenWellOps(widget.rect);
  const shown = widget.rows.slice(widget.top, widget.top + visible);
  const selectedRow = widget.selected - widget.top;
  if (selectedRow >= 0 && selectedRow < visible) ops.push(...listSelectionOps(well, selectedRow));
  shown.forEach((row, index) => {
    const chosen = index === selectedRow;
    const style: TextStyle = { body: chosen ? LIST_SELECTED_TEXT : focused ? 238 : LIST_TEXT };
    const y = well.y + 1 + index * LIST_ROW_PITCH;
    ops.push({ kind: "text", font: LIST_FONT, text: row.label, x: well.x + LIST_ROW_TEXT_INSET_X, y, style });
    if (row.trailing !== undefined) {
      ops.push({ kind: "text", font: LIST_FONT, text: row.trailing, x: well.x + well.w - 14, y, style });
    }
  });
  return ops;
}

/** The two 17×17 scroll buttons beside a list, drawn only when the list is longer than its well. */
export function listScrollOps(rect: Rect, rows: number, visible: number): DrawOp[] {
  if (rows <= visible) return [];
  const x = rect.x + rect.w + 7;
  const up: Rect = { x, y: rect.y + 4, w: ARROW_SIZE, h: ARROW_SIZE };
  const down: Rect = { x, y: rect.y + rect.h - 4 - ARROW_SIZE, w: ARROW_SIZE, h: ARROW_SIZE };
  return [
    ...raisedPlateOps({ x: rect.x + rect.w + 2, y: rect.y, w: 27, h: rect.h }),
    ...boardButtonOps(up, { label: "", enabled: true }, false, EMPTY_FONT),
    ...arrowOps(up, "up"),
    ...boardButtonOps(down, { label: "", enabled: true }, false, EMPTY_FONT),
    ...arrowOps(down, "down"),
  ];
}

/** The scroll buttons carry no label, so the atlas they measure against never matters. */
const EMPTY_FONT = { id: "FONT1", meta: { fontWidth: 0, fontHeight: 0, baseLine: 0, startSymbol: 32, numSymbols: 0, glyphs: [] }, glyphs: new Map() } as FontAtlas;

export function widgetOps(widget: Widget, fonts: WidgetFonts, focused: boolean): DrawOp[] {
  switch (widget.kind) {
    case "button":
      return boardButtonOps(widget.rect, { label: widget.label, enabled: widget.enabled }, focused, fonts.heading);
    case "plate":
      return raisedPlateOps(widget.rect);
    case "orangePlate":
      // Corners cut and rivets set in — `plate-chrome.ts`'s stamps, measured off
      // `mission-debriefing.png` (G55). This is the only screen that draws one.
      return chamferedPlateOps(widget.rect);
    case "well":
      return sunkenWellOps(widget.rect, widget.face, widget.orange ? ORANGE_SUNKEN_RINGS : undefined);
    case "text":
      return [textOp(widget.role, widget.text, widget.x, widget.y)];
    case "prose":
      return widget.block.lines.map((line, i) =>
        textOp(widget.block.role, line, widget.block.x, widget.block.y + i * widget.block.pitch),
      );
    case "list":
      return [...listOps(widget, focused), ...listScrollOps(widget.rect, widget.rows.length, Math.floor((widget.rect.h - 4) / LIST_ROW_PITCH))];
    case "value":
      return valueOps(widget, fonts, focused);
    case "toggle":
      return toggleOps(widget, fonts, focused);
    case "slot":
      return [
        ...slotOps(widget.rect, focused && widget.action !== undefined),
        textOp("slot", widget.text, widget.rect.x + SLOT_TEXT_INSET, widget.rect.y + 2),
      ];
    case "wellText":
      return [
        {
          kind: "text",
          font: LIST_FONT,
          text: widget.text,
          x:
            widget.align === "left"
              ? widget.rect.x + WELL_TEXT_INSET
              : plateTextX(fonts.small, widget.text, { ...widget.rect, x: widget.rect.x + 2, w: widget.rect.w - 4 }),
          y: widget.rect.y + 3,
          style: { body: WELL_TEXT },
        },
      ];
    case "picture":
      return [{ kind: "sprite", sheet: widget.sheet, frame: widget.frame, x: widget.x, y: widget.y, flipH: false }];
    case "rules":
      return ruleFanOps(widget.x, widget.y, widget.length, widget.toward);
    case "underline": {
      // The orange ramp runs the other way, so the engraved pair does too:
      // `mission-debriefing.png` rules VICTORIES 243 over 252.
      const [dark, light] = widget.orange ? [243, 252] : [235, 226];
      return [
        fill({ x: widget.x, y: widget.y, w: widget.w, h: 1 }, dark as number),
        fill({ x: widget.x, y: widget.y + 1, w: widget.w, h: 1 }, light as number),
      ];
    }
  }
}
